import { useEffect, useState } from 'react';
import { Box } from 'src/components/Box';
import { ExpandableText } from 'src/components/ExpandableText';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';

import { CheckinBlockerSessionDetails } from './_types';

type BookingSessionDescriptionProps = {
  sessionDetails: CheckinBlockerSessionDetails;
  /** When true, show service name in-content (mobile / when modal chrome has no title). */
  showServiceName?: boolean;
};

/**
 * Left-column session summary: optional title, thumbnail, and description
 * (mirrors Ember Landing::Description + LineClamp in the booking session modal).
 */
export function BookingSessionDescription({
  sessionDetails,
  showServiceName = false,
}: BookingSessionDescriptionProps) {
  const description = sessionDetails.serviceDescription?.trim();
  const imageUrl = sessionDetails.imageUrl?.trim();
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = !!imageUrl && !imageFailed;
  const hasDescription = !!description;

  useEffect(() => {
    setImageFailed(false);
  }, [imageUrl]);

  if (!showServiceName && !imageUrl && !hasDescription) {
    return null;
  }

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      {showServiceName ? (
        <Typography variant="h5" component="h2">
          {sessionDetails.serviceName}
        </Typography>
      ) : null}

      {imageUrl ? (
        <Box
          component="img"
          src={imageUrl}
          alt={sessionDetails.serviceName}
          onError={() => setImageFailed(true)}
          sx={{
            width: '100%',
            // No usable image: height 0 so we don't reserve the 184px thumbnail slot
            height: showImage ? 184 : 0,
            objectFit: 'cover',
            borderRadius: 1,
            display: showImage ? 'block' : 'none',
            overflow: 'hidden',
          }}
        />
      ) : null}

      {hasDescription ? (
        <ExpandableText showMore lines={3}>
          <Typography variant="body2" color="text.secondary" component="span">
            {description}
          </Typography>
        </ExpandableText>
      ) : null}
    </Stack>
  );
}
